import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DeviceService {
  private platformSubject = new BehaviorSubject<string | null>(null);
  public platform$: Observable<string | null> = this.platformSubject.asObservable();

  get platform(): string | null {
    return this.platformSubject.getValue();
  }

  constructor() {
    this.detectPlatform();

    // Retry detection after short delays for late NativeBridge injection
    // On some Android TV devices, NativeBridge is injected AFTER Angular initializes
    if (!this.platform) {
      setTimeout(() => this.detectPlatform(), 100);
      setTimeout(() => this.detectPlatform(), 500);
      setTimeout(() => this.detectPlatform(), 1000);
    }
  }

  private detectPlatform(): void {
    if (this.platform) {
      return;
    }

    // 1. NativeBridge (Android TV custom bridge — highest confidence)
    if ((window as any).NativeBridge?.getPlatform) {
      const platform = (window as any).NativeBridge.getPlatform();
      this.platformSubject.next(platform);
      this.applyTVViewport();
      return;
    }

    // 2. window.AndroidTV (Android TV generic)
    if ((window as any).AndroidTV) {
      this.platformSubject.next('android_tv');
      this.applyTVViewport();
      return;
    }

    // 3. Samsung Tizen detection
    if ((window as any).tizen) {
      this.platformSubject.next('tizen');
      return;
    }

    // 4. LG webOS detection
    if ((window as any).webOS || (window as any).webOSSystem) {
      this.platformSubject.next('webos');
      return;
    }

    // 5. User-agent fallbacks for Tizen and webOS (lowest confidence)
    const userAgent = navigator.userAgent;

    // Tizen UA check - must include both 'Tizen' and 'SmartTV' to avoid mobile false positives
    if (userAgent.includes('Tizen') && userAgent.includes('SmartTV')) {
      this.platformSubject.next('tizen');
      return;
    }

    // webOS UA check - must include SmartTV or LG to avoid mobile false positives
    if (userAgent.match(/Web0S|webOS/i) && (userAgent.includes('SmartTV') || userAgent.includes('LG'))) {
      this.platformSubject.next('webos');
      return;
    }
  }

  /**
   * Override viewport meta tag for TV to render at 1280px CSS width.
   * Without this, Android TV WebView at 213dpi reports ~960px viewport,
   * making everything appear zoomed compared to a desktop browser.
   */
  private applyTVViewport(): void {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=1280');
    }
  }

  isTV(): boolean {
    const p = this.platform;
    return p === 'android_tv' || p === 'tizen' || p === 'webos';
  }

  isTV$(): Observable<boolean> {
    return this.platform$.pipe(
      map(p => p === 'android_tv' || p === 'tizen' || p === 'webos')
    );
  }

  toggleTV(): void {
    if (!isDevMode()) return;
    // Cycle through TV platforms for dev testing: null -> android_tv -> null
    if (this.platform === 'android_tv') {
      this.platformSubject.next(null);
    } else {
      this.platformSubject.next('android_tv');
    }
  }

  getPlatformName(): string {
    switch (this.platform) {
      case 'android_tv': return 'Android TV';
      case 'tizen': return 'Samsung Tizen';
      case 'webos': return 'LG webOS';
      default: return 'Web';
    }
  }

  isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  isTablet(): boolean {
    return window.innerWidth > 768 && window.innerWidth <= 1024;
  }

  isDesktop(): boolean {
    return window.innerWidth > 1024;
  }

  getPlatformType(): string {
    if ((window as any)?.webkit) return 'IOS';

    const ua = navigator.userAgent;
    const isApple = /Macintosh|iPad|iPhone|iPod/.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (isApple && isSafari) return 'SAFARI';

    if (this.platform === 'android_tv') {
      if (/AFT/.test(ua)) return 'FIRE_TV';
      return 'ANDROID_TV';
    }
    if (this.platform === 'tizen') return 'TIZEN';
    if (this.platform === 'webos') return 'WEBOS';

    return 'WEB';
  }
}

